<template>
  <div id="lm-leaflet-map">
    <l-map
    ref="mapObject"
    class="map_object"
    v-bind="map.LMap.props"
    :center="getCenter"
    @ready="onReady()"
    @locationfound="onLocationFound"
    >
      <l-tile-layer
      v-for="tileProvider in tileProviders"
      :key="tileProvider.name"
      :name="tileProvider.name"
      :visible="tileProvider.visible"
      :url="tileProvider.url"
      :attribution="tileProvider.attribution"
      layer-type="base"/>

      <l-circle-marker v-if="centerMarkerVisible"
        :lat-lng="getCenter"
        :radius="centerMarkerRadius"
        :color="centerMarkerColor"
      />
      <l-control-layers position="topright" ></l-control-layers>
      <l-control-scale position="bottomleft" :imperial="false" :metric="true"></l-control-scale>
      <l-control position="topleft" >
        <b-button class="btn_map" variant="primary" v-b-tooltip.hover.right.d350  title="Geolocalización" @click="clickGeolocation">
          <font-awesome-icon icon="map-marker" />
        </b-button>
      </l-control>
      <l-control position="topleft" >
      <b-button class="btn_map" variant="primary"  v-b-tooltip.hover.right.d350 :title="enlaceCopiado ? 'Enlace copiado!' : 'Compartir'" @click="clickShare">
        <font-awesome-icon icon="share-alt" />
      </b-button>
      </l-control>

      <v-marker-cluster>
        <div v-for="(marker, index) in markers" :key="index">
          <l-marker :lat-lng="marker.latLng">
            <l-icon
              :icon-size="dynamicSize"
              :icon-anchor="dynamicAnchor"
              v-if="marker.iconurl"
              icon-url="https://vue2-leaflet.netlify.app/images/baseball-marker.png"
              >
            </l-icon>
            <l-popup v-if="map.LPopup" :options="map.LPopup.props.options">
              <div class="popup-content">
                <h6 class="my-3">{{marker[map.LPopup.props.title]}}</h6>
                <div class="mb-1">
                  <div v-for="(content, index2) in map.LPopup.props.content" :key="`content-${index2}`">
                    <div class="mb-2" v-if="content.type==='text' && marker[content.field]">
                    <span class="description" v-if="content.description">{{ content.field + ': ' }}</span>
                    {{ marker[content.field]}}
                    </div>
                  </div>
                  <br>
                </div>
                <b-button-group class="button_group_popup" v-for="(action, index) in marker.actions" :key="index">
                  <router-link :to="action.arguments.to" v-if="action.arguments.to">
                    <b-button variant="primary">
                      <font-awesome-icon :icon="action.arguments.fontAwesomeIcon" />
                      {{action.arguments.tooltip}}
                     </b-button>
                    </router-link>
                </b-button-group>
              </div>
            </l-popup>
          </l-marker>
        </div>
      </v-marker-cluster>
    </l-map>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LControl, LControlLayers, LControlScale, LIcon, LCircleMarker } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'

export default {
  name: 'Map',
  components: {
    'l-map': LMap,
    'l-tile-layer': LTileLayer,
    'l-marker': LMarker,
    'l-popup': LPopup,
    'l-control': LControl,
    'l-control-layers': LControlLayers,
    'l-control-scale': LControlScale,
    'l-icon': LIcon,
    'l-circle-marker': LCircleMarker
  },
  data () {
    return {
      mapObject: null,
      permissions: null,
      center: null,
      enlaceCopiado: false,
      iconSize: 'LIcon' in this.map ? this.map.LIcon.iconSize : 36,
      centerMarkerVisible: true,
      centerMarkerColor: 'red',
      centerMarkerRadius: 6,
      tileProviders: [
        {
          name: 'Mapa',
          visible: true,
          attribution:
            '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
          url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        },
        {
          name: 'Satelite',
          visible: false,
          url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        },
        {
          name: 'Topográfico',
          visible: false,
          url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png'
        }
      ]
    }
  },
  props: {
    markers: {
      type: [Array, String],
      default: () => [],
      required: true
    },
    map: {
      type: Object,
      default: () => [],
      required: false
    }
  },
  computed: {
    dynamicSize () {
      return [this.iconSize, this.iconSize * 1.15]
    },
    dynamicAnchor () {
      return [this.iconSize / 2, this.iconSize * 1.15]
    },
    getCenter () {
      return this.center ? this.center : this.map.LMap.props.center
    }
  },
  methods: {
    async onReady () {
      this.mapObject = this.$refs.mapObject.mapObject
      const lat = this.$route.query.lat
      const lng = this.$route.query.lng
      const zoom = this.$route.query.zoom
      this.center = this.map.LMap.props.center
      // Solo si se indica desactivamos el zoom con el scrollwheel del mouse
      if (!this.map.LMap?.props?.scrollWheelZoomEnabled) {
        this.mapObject.scrollWheelZoom.disable()
      }

      if (this.map.LMCenterMarker?.props?.hidden === true) {
        this.centerMarkerVisible = false
      }
      if (this.map.LMCenterMarker?.props?.radius) {
        this.centerMarkerRadius = this.map.LMCenterMarker.props.radius
      }
      if (this.map.LMCenterMarker?.props?.color) {
        this.centerMarkerColor = this.map.LMCenterMarker.props.color
      }

      // Solo si se indica se activa la geolocalizacion del browser
      if (this.map.LMap?.props?.enableBrowserGeolocation) {
        const result = await navigator.permissions.query({ name: 'geolocation' })
        this.permissions = result.state

        // Si está aceptada la geolocalización y no se pasa por la url lat, lng y zoom, te geolocaliza
        if (result.state === 'granted' && !lat && !lng && !zoom) this.mapObject.locate({ setView: true, maxZoom: 10 })
      }

      // Si se pasa por url la lat, lng y zoom te lleva a esa posición en el mapa
      if (lat && lng && zoom) this.mapObject.flyTo([lat, lng], zoom)

      if (!this.map.LMDraw?.props?.disabled) {
        const map = this.mapObject
        const drawnItems = new window.L.FeatureGroup()
        this.mapObject.addLayer(drawnItems)

        const drawControlFull = new window.L.Control.Draw({
          draw: {
            polyline: false,
            polygon: this.map.LMDraw?.props?.polygon,
            rectangle: this.map.LMDraw?.props?.rectangle,
            circle: this.map.LMDraw?.props?.circle,
            circlemarker: this.map.LMDraw?.props?.circlemarker,
            marker: this.map.LMDraw?.props?.marker
          },
          edit: {
            featureGroup: drawnItems
          }
        })
        const drawControlEditOnly = new window.L.Control.Draw({
          edit: {
            featureGroup: drawnItems
          },
          draw: false
        })
        map.addControl(drawControlFull)

        map.on(window.L.Draw.Event.CREATED, (e) => {
          drawnItems.addLayer(e.layer)
          map.removeControl(drawControlFull)
          map.addControl(drawControlEditOnly)
          if (Array.isArray(e.layer._latlngs)) {
            // Se dibujo un polígono
            for (const polygonCoordinates of e.layer._latlngs) {
              const polygon = polygonCoordinates.map(objCoordinates => {
                return {
                  lat: objCoordinates.lat,
                  lng: objCoordinates.lng
                }
              })
              this.$router.push({
                query: {
                  ...this.$route.query,
                  polygons: JSON.stringify(polygon)
                }
              })
            }
          } else if (e.layer?._mRadius) {
            // Se dibujo un circulo
            this.$router.push({
              query: {
                ...this.$route.query,
                lat: e.layer._latlng.lat,
                lng: e.layer._latlng.lng,
                radius: e.layer._mRadius,
                zoom: zoom
              }
            })
          }
        })

        map.on(window.L.Draw.Event.DELETED, (e) => {
          if (drawnItems.getLayers().length === 0) {
            map.removeControl(drawControlEditOnly)
            map.addControl(drawControlFull)
            this.removeGeoArgsFromQuery()
          }
        })
      }
    },
    onLocationFound (location) {
      this.center = [location.latitude, location.longitude]
    },
    clickGeolocation () {
      this.mapObject.locate({ setView: true, maxZoom: this.map.LMap.props.zoom })
    },
    clickShare () {
      this.pushQuery()
      this.copyUrl()
    },
    pushQuery () {
      const lat = this.mapObject.getCenter().lat
      const lng = this.mapObject.getCenter().lng
      const zoom = this.mapObject.getZoom()
      this.center = [lat, lng]
      this.$router.push({ query: { ...this.$route.query, lat: lat, lng: lng, zoom: zoom } })
    },
    removeGeoArgsFromQuery () {
      const query = Object.assign({}, this.$route.query)
      for (const key of ['lat', 'lng', 'radius', 'polygons']) {
        if (query[key]) {
          delete query[key]
        }
      }
      this.$router.push({ query: { ...query } })
    },
    async copyUrl () {
      const url = window.location.href
      await this.$copyText(url)
      this.enlaceCopiado = true
      setTimeout(() => {
        this.enlaceCopiado = false
      }, 1500)
    }
  }
}
</script>

<style lang="css" scoped>
.map_object{
  width: 100%;
  height: 66vh;
  z-index:0;
}
.btn_map{
  display: flex;
  justify-content: center;
  align-items: center;
  background:#fff;
  color: #313a46;
  border: 0px;
  width: 26px;
  height: 26px;
  font-size: 1em;
  border-radius: 4px;
  -webkit-box-shadow: 0 1px 5px rgba(0,0,0,0.65);
  -moz-box-shadow: 0 1px 5px rgba(0,0,0,0.65);
  box-shadow: 0 1px 5px rgba(0,0,0,0.65);
}
.btn_map:hover{
 background:#f4f4f4;
 color: #313a46;
}
.popup-content{
  font-size:1.2em;
}
.button_group_popup button{
  margin-top: 0px;
  margin-bottom: 5px;
  margin-right: 5px;
  font-size:12px;
  color: #fff;
  border:0px solid #ddd;
  box-shadow:none;
}
.description{
  font-weight: bold;
  text-transform: capitalize;
}
</style>
